import React, { useState, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Button, Card, Form, Navbar, Nav, Carousel, Modal, Badge, Accordion, Alert, Spinner } from 'react-bootstrap';
import { FaBuilding, FaHotel, FaUtensils, FaHome, FaShoppingCart, FaHouseUser, FaClinicMedical, FaPhoneAlt, FaRobot, FaChartLine, FaLightbulb, FaChevronRight, FaCheckCircle, FaRocket, FaExclamationTriangle } from 'react-icons/fa';
import { MdSecurity, MdOutlineSupportAgent } from 'react-icons/md';
import { BsFillPatchCheckFill } from 'react-icons/bs';
import ChatBot from './components/ChatBot';
import CalendarComponent from './components/Calendar';
import './App.css';
import axios from 'axios';
import { validatePhoneNumber, formatPhoneNumber, phoneErrorMsg } from './utils/phoneUtils';
import { getIndustrySlug, validateIndustry, industryErrorMsg } from './utils/industryUtils';

function App() {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showIndustryModal, setShowIndustryModal] = useState(null);
  const [showChatBot, setShowChatBot] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
  });
  const [phoneInput, setPhoneInput] = useState('');
  const [callStatus, setCallStatus] = useState('idle');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [callId, setCallId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScrolled(window.scrollY > 50);
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  const industries = [
    {
      title: 'Apartments',
      desc: 'Automate maintenance requests and resident communications.',
      icon: <FaBuilding size={32} className="mb-2 text-primary" />,
      color: 'primary',
      exampleDialogue: ["Resident: My heater is broken.", "Kleo: I'll dispatch a technician today."],
      key: 'apartment'
    },
    {
      title: 'Hotels',
      desc: 'Enhance guest services with AI-powered concierge.',
      icon: <FaHotel size={32} className="mb-2 text-warning" />,
      color: 'warning',
      exampleDialogue: ["Guest: Any rooms available?", "Kleo: Yes, we have a suite ready."],
      key: 'hotel'
    },
    {
      title: 'Restaurants',
      desc: 'Manage bookings and answer customer queries seamlessly.',
      icon: <FaUtensils size={32} className="mb-2 text-danger" />,
      color: 'danger',
      exampleDialogue: ["Customer: Gluten-free options?", "Kleo: We have several dishes."],
      key: 'restaurant'
    },
    {
      title: 'Vacation Rentals',
      desc: 'Handle guest inquiries and streamline check-ins.',
      icon: <FaHome size={32} className="mb-2 text-success" />,
      color: 'success',
      exampleDialogue: ["Guest: Can I check in early?", "Kleo: I'll arrange it for you."],
      key: 'vacation'
    },
    {
      title: 'E-commerce',
      desc: 'Support customers with order tracking and FAQs.',
      icon: <FaShoppingCart size={32} className="mb-2 text-info" />,
      color: 'info',
      exampleDialogue: ["Customer: Where's my order?", "Kleo: It's arriving tomorrow."],
      key: 'ecommerce'
    },
    {
      title: 'Real Estate',
      desc: 'Qualify leads and schedule viewings automatically.',
      icon: <FaHouseUser size={32} className="mb-2 text-secondary" />,
      color: 'secondary',
      exampleDialogue: ["Client: Any open houses?", "Kleo: I can book you for Saturday."],
      key: 'real_estate'
    },
    {
      title: 'Medical Offices',
      desc: 'Book appointments and manage patient inquiries.',
      icon: <FaClinicMedical size={32} className="mb-2 text-dark" />,
      color: 'dark',
      exampleDialogue: ["Patient: Next appointment?", "Kleo: Tuesday at 10 AM works."],
      key: 'medical'
    },
  ];

  const features = [
    { title: '24/7 Availability', desc: 'Handle calls anytime, anywhere.', icon: <FaPhoneAlt size={24} /> },
    { title: 'Natural Conversations', desc: 'Human-like interactions with NLP.', icon: <FaRobot size={24} /> },
    { title: 'Performance Analytics', desc: 'Monitor metrics for insights.', icon: <FaChartLine size={24} /> },
    { title: 'Smart Learning', desc: 'Adapts with machine learning.', icon: <FaLightbulb size={24} /> },
    { title: 'Enterprise Security', desc: 'Top-tier data protection.', icon: <MdSecurity size={24} /> },
    { title: 'Ethical AI', desc: 'Transparent and responsible.', icon: <BsFillPatchCheckFill size={24} /> },
  ];

  const testimonials = [
    {
      quote: "Kleotech cut our front desk calls by 70% and boosted guest satisfaction.",
      author: "Sarah Johnson",
      position: "Operations Manager, Grand Hotels",
      rating: 5,
    },
    {
      quote: "Kleo manages 90% of our bookings, freeing staff for guests.",
      author: "Michael Chen",
      position: "Owner, Coastal Restaurants",
      rating: 5,
    },
    {
      quote: "Seamless setup and instant ROI. Kleotech is a game-changer.",
      author: "David Rodriguez",
      position: "Property Manager, Urban Living",
      rating: 4,
    },
  ];

  const faqs = [
    {
      question: "How quickly can we implement Kleotech?",
      answer: "Most setups take 2-4 weeks, tailored to your systems.",
    },
    {
      question: "What languages does Kleo support?",
      answer: "English and Spanish, with more planned.",
    },
    {
      question: "Does it integrate with our software?",
      answer: "Yes, via APIs for CRM, PMS, and booking systems.",
    },
    {
      question: "How is pricing structured?",
      answer: "Flexible plans based on call volume and features.",
    },
  ];

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmitDemo = useCallback((e) => {
    e.preventDefault();
    if (window.gtag) {
      window.gtag('event', 'demo_request', {
        event_category: 'engagement',
        event_label: formData.industry || 'unknown',
      });
    }
    console.log('Demo requested:', formData);
    setShowDemoModal(false);
    alert("Thank you! We'll contact you soon to schedule your demo.");
    setFormData({ name: '', email: '', company: '', industry: '' });
  }, [formData]);

  const requestIndustryDemo = useCallback(async (industry) => {
    setCallStatus('loading');
    try {
      const response = await axios.post('https://calldemo-nsqvlwjt7a-uc.a.run.app', {
        to: phoneInput,
        useCase: industry.key
      });
      console.log('Call initiated:', response.data);
      
      // Check if the response contains call_id and agent_id
      if (response.data.call_id && response.data.agent_id) {
        setCallStatus('success');
        setCallId(response.data.call_id);
        setAgentId(response.data.agent_id);
        alert("✅ Kleo is calling you now!");
      } else {
        setCallStatus('error');
        setErrorMessage('Failed to initiate call: Invalid response format');
        alert("❌ Failed to initiate call. Please try again later.");
      }
    } catch (error) {
      console.error('Demo request failed:', error);
      setCallStatus('error');
    }
  }, [phoneInput]);

  const scrollTo = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth',
      });
    }
  }, []);

  const renderStars = (count) => {
    return Array(count).fill(0).map((_, i) => (
      <span key={i} className="text-warning" aria-label="Star rating">★</span>
    ));
  };

  const triggerDebugModal = () => {
    setPhoneInput('+13465439208');
    setShowDemoModal(true);
    setShowIndustryModal('apartment');
  };

  return (
    <div className="overflow-hidden">
      {/* Debug Panel - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 9999,
          padding: '10px',
          background: debugMode ? '#ff5722' : '#607d8b',
          color: 'white',
          borderRadius: '5px'
        }}>
          <button 
            onClick={() => setDebugMode(!debugMode)}
            style={{ background: 'none', border: 'none', color: 'white' }}
          >
            {debugMode ? '🔴 Debug ON' : '⚪ Debug OFF'}
          </button>
          {debugMode && (
            <div style={{ marginTop: '10px' }}>
              <div>Phone: {phoneInput}</div>
              <div>Industry: {JSON.stringify(showIndustryModal)}</div>
            </div>
          )}
        </div>
      )}

      {/* Navbar */}
      <Navbar
        bg={scrolled ? 'white' : 'transparent'}
        expand="lg"
        fixed="top"
        className={`py-3 ${scrolled ? 'shadow-sm' : ''}`}
        style={{ transition: 'all 0.3s ease' }}
      >
        <Container>
          <Navbar.Brand href="#" className="fw-bold text-primary d-flex align-items-center">
            <FaRobot className="me-2" size={24} aria-hidden="true" />
            <span className="d-none d-sm-inline">CallManager.ai</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto align-items-center">
              <Nav.Link onClick={() => scrollTo('features')} className="mx-2">
                Features
              </Nav.Link>
              <Nav.Link onClick={() => scrollTo('industries')} className="mx-2">
                Industries
              </Nav.Link>
              <Nav.Link onClick={() => scrollTo('how-it-works')} className="mx-2">
                How It Works
              </Nav.Link>
              <Nav.Link onClick={() => scrollTo('testimonials')} className="mx-2">
                Testimonials
              </Nav.Link>
              <Button
                variant="primary"
                className="ms-3 rounded-pill px-4 py-2"
                onClick={() => setShowDemoModal(true)}
                aria-label="Request a demo"
              >
                Book a Free Consultation
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section className="py-5 text-start" style={{ 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '100px !important'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="pe-5">
              <Badge bg="primary" pill className="mb-3 px-3 py-2 fw-normal">
                <BsFillPatchCheckFill className="me-2" /> AI Voice Assistant
              </Badge>
              <h1 className="display-4 fw-bold text-dark mb-4">
                Transform Your Customer Service with <span className="text-primary">CallManager.ai</span>
              </h1>
              <p className="lead text-muted mb-4">
                <span className="fw-bold text-primary">👨‍💼 Your 24/7 AI-Powered Customer Concierge</span><br /><br />
                Kleo revolutionizes customer engagement with intelligent, real-time support — handling calls, bookings, and inquiries with a personal touch.<br /><br />
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <FaCheckCircle className="text-success me-2" />
                    <span className="fw-bold">No more missed calls</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <FaRocket className="text-warning me-2" />
                    <span className="fw-bold">Instant customer support</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaChartLine className="text-info me-2" />
                    <span className="fw-bold">Reduced operational costs</span>
                  </div>
                </div>
                Deliver world-class experiences while cutting costs. Kleo never sleeps — your customers never wait.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Button 
                  variant="warning" 
                  size="lg" 
                  className="rounded-pill px-5 py-3 fw-bold"
                  onClick={() => setShowDemoModal(true)}
                >
                  Book a Free Consultation <FaChevronRight className="ms-2" />
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="lg" 
                  className="rounded-pill px-4 py-3"
                  onClick={() => scrollTo('how-it-works')}
                >
                  How It Works
                </Button>
              </div>
              <div className="mt-4 d-flex align-items-center">
                <div className="d-flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="rounded-circle bg-white border border-2 border-primary d-flex align-items-center justify-content-center" 
                      style={{ width: 36, height: 36, marginLeft: i > 1 ? -10 : 0, zIndex: 5 - i }}>
                      <span className="text-primary fw-bold">{i}</span>
                    </div>
                  ))}
                </div>
                <div className="ms-3">
                  <p className="mb-0 small text-muted">Trusted by 500+ businesses worldwide</p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="mt-5 mt-lg-0">
              <div className="position-relative">
                <img 
                  src="/pictures/kleomain.png" 
                  alt="Kleo AI Assistant" 
                  className="img-fluid rounded-4 shadow-lg w-100" 
                  style={{ maxHeight: '450px', objectFit: 'cover' }} 
                />
                <div className="position-absolute bottom-0 start-0 bg-white p-3 rounded-3 shadow-sm m-3" style={{ maxWidth: '200px' }}>
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-primary rounded-circle p-2 me-2">
                      <FaRobot className="text-white" />
                    </div>
                    <div>
                      <p className="mb-0 fw-bold">Kleo Assistant</p>
                      <p className="mb-0 small text-muted">Online</p>
                    </div>
                  </div>
                  <p className="mb-0 small">"Hello! I'm Kleo, how can I help you today?"</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Live Demo Numbers Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="justify-content-center mb-4">
            <Col lg={8} className="text-center">
              <h2 className="fw-bold mb-3">
                <FaPhoneAlt className="text-primary me-2" />
                Try Kleo AI Live
              </h2>
              <p className="lead text-muted mb-4">
                Choose your industry and call the number below to experience Kleo:
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <FaBuilding size={32} className="text-primary mb-3" />
                  <h5 className="fw-bold mb-3">Apartments</h5>
                  <a href="tel:+13467702556" className="fw-bold text-decoration-none">
                    +1 (346) 770-2556
                  </a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <FaHotel size={32} className="text-warning mb-3" />
                  <h5 className="fw-bold mb-3">Hotels</h5>
                  <a href="tel:+13465973040" className="fw-bold text-decoration-none">
                    +1 (346) 597-3040
                  </a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <FaUtensils size={32} className="text-danger mb-3" />
                  <h5 className="fw-bold mb-3">Restaurants</h5>
                  <a href="tel:+13465918044" className="fw-bold text-decoration-none">
                    +1 (346) 591-8044
                  </a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <FaClinicMedical size={32} className="text-dark mb-3" />
                  <h5 className="fw-bold mb-3">Medical Offices</h5>
                  <a href="tel:+13465531369" className="fw-bold text-decoration-none">
                    +1 (346) 553-1369
                  </a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <FaShoppingCart size={32} className="text-info mb-3" />
                  <h5 className="fw-bold mb-3">E-commerce</h5>
                  <a href="tel:+19786446969" className="fw-bold text-decoration-none">
                    +1 (978) 644-6969
                  </a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <FaHouseUser size={32} className="text-secondary mb-3" />
                  <h5 className="fw-bold mb-3">Real Estate</h5>
                  <a href="tel:+13464766113" className="fw-bold text-decoration-none">
                    +1 (346) 476-6113
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trusted By Section */}
      <section className="py-4 bg-light">
        <Container>
          <p className="text-center text-muted mb-4">TRUSTED BY INDUSTRY LEADERS</p>
          <Row className="align-items-center justify-content-center">
            {['Company A', 'Company B', 'Company C', 'Company D', 'Company E'].map((company, i) => (
              <Col key={i} xs={6} sm={4} md={2} className="text-center mb-3 mb-md-0">
                <div className="bg-white p-3 rounded-3 shadow-sm h-100 d-flex align-items-center justify-content-center">
                  <span className="fw-bold text-muted">{company}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="py-5">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <Badge bg="light" text="dark" pill className="mb-3 px-3 py-2 fw-normal">
                WHY CHOOSE CALLMANAGER.AI
              </Badge>
              <h2 className="fw-bold mb-3">Powerful Features for Exceptional Service</h2>
              <p className="text-muted">
                Advanced AI delivers seamless customer experiences with integrity.
              </p>
            </Col>
          </Row>
          <Row>
            {features.map((feature, i) => (
              <Col md={6} lg={4} key={i} className="mb-4">
                <Card className="h-100 border-0 shadow-sm hover-shadow transition-all">
                  <Card.Body className="p-4">
                    <div className="bg-light rounded-circle p-3 mb-3 d-inline-flex">
                      {React.cloneElement(feature.icon, { className: 'text-primary' })}
                    </div>
                    <Card.Title className="fw-bold mb-3">{feature.title}</Card.Title>
                    <Card.Text className="text-muted">{feature.desc}</Card.Text>
                    <Button variant="link" className="ps-0 text-decoration-none text-primary">
                      Learn more <FaChevronRight className="ms-1" size={12} />
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-5 bg-light">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <Badge bg="light" text="dark" pill className="mb-3 px-3 py-2 fw-normal">
                INDUSTRY SOLUTIONS
              </Badge>
              <h2 className="fw-bold mb-3">Tailored AI for Your Industry</h2>
              <p className="text-muted">
                Kleotech adapts to your business with specialized AI models.
              </p>
            </Col>
          </Row>
          <Row>
            {industries.map((industry, i) => (
              <Col md={6} lg={3} key={i} className="mb-4">
                <Card className="h-100 border-0 shadow-sm hover-shadow transition-all">
                  <Card.Body className="p-4 text-center">
                    <div className={`bg-${industry.color} bg-opacity-10 rounded-circle p-3 mb-3 d-inline-flex`}>
                      {industry.icon}
                    </div>
                    <Card.Title className="fw-bold mb-3">{industry.title}</Card.Title>
                    <Card.Text className="text-muted">{industry.desc}</Card.Text>
                    <Button
                      variant={`outline-${industry.color}`}
                      className="mt-2"
                      onClick={() => {
                        setShowIndustryModal(industry);
                        setPhoneInput('');
                        setCallStatus('idle');
                      }}
                      aria-label={`Request live demo for ${industry.title}`}
                    >
                      <FaPhoneAlt className="me-2" /> Live Demo
                    </Button>
                    <div className="mt-3 bg-light p-2 rounded small">
                      {industry.exampleDialogue.map((line, j) => (
                        <p key={j} className={j % 2 === 0 ? 'text-start' : 'text-end'}>
                          {line}
                        </p>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-5 position-relative">
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary bg-opacity-5"></div>
        <Container className="position-relative">
          <Row className="align-items-center">
            <Col lg={6} className="pe-lg-5 mb-5 mb-lg-0">
              <Badge bg="primary" pill className="mb-3 px-3 py-2 fw-normal">
                OUR PROCESS
              </Badge>
              <h2 className="fw-bold mb-4">Simple Setup, Fast Results</h2>
              <p className="text-muted mb-4">
                Integrate Kleotech effortlessly while we handle the tech.
              </p>
              <div className="d-flex mb-4">
                <div className="flex-shrink-0">
                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                    <span className="text-white fw-bold">1</span>
                  </div>
                </div>
                <div className="ms-4">
                  <h5 className="fw-bold mb-2">Discovery Call</h5>
                  <p className="text-muted mb-0">We assess your needs and systems.</p>
                </div>
              </div>
              <div className="d-flex mb-4">
                <div className="flex-shrink-0">
                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                    <span className="text-white fw-bold">2</span>
                  </div>
                </div>
                <div className="ms-4">
                  <h5 className="fw-bold mb-2">Custom Setup</h5>
                  <p className="text-muted mb-0">We configure Kleo for your workflows.</p>
                </div>
              </div>
              <div className="d-flex">
                <div className="flex-shrink-0">
                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                    <span className="text-white fw-bold">3</span>
                  </div>
                </div>
                <div className="ms-4">
                  <h5 className="fw-bold mb-2">Launch & Optimize</h5>
                  <p className="text-muted mb-0">Go live and refine with data.</p>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <Card className="border-0 shadow-lg overflow-hidden">
                <Card.Body className="p-0">
                  <div className="d-flex align-items-center bg-primary text-white p-4">
                    <FaRobot size={24} className="me-3" aria-hidden="true" />
                    <div>
                      <h5 className="mb-0 fw-bold">Kleo Assistant</h5>
                      <small>Conversation Preview</small>
                    </div>
                  </div>
                  <div className="p-4" style={{ height: '300px', overflowY: 'auto' }}>
                    <div className="d-flex mb-3">
                      <div className="bg-light rounded-3 p-3 me-3" style={{ maxWidth: '70%' }}>
                        <p className="mb-0">Hello! I'm Kleo. How can I assist you?</p>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mb-3">
                      <div className="bg-primary text-white rounded-3 p-3" style={{ maxWidth: '70%' }}>
                        <p className="mb-0">I need a room for next weekend.</p>
                      </div>
                    </div>
                    <div className="d-flex mb-3">
                      <div className="bg-light rounded-3 p-3 me-3" style={{ maxWidth: '70%' }}>
                        <p className="mb-0">Which property interests you?</p>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mb-3">
                      <div className="bg-primary text-white rounded-3 p-3" style={{ maxWidth: '70%' }}>
                        <p className="mb-0">Downtown, with a city view.</p>
                      </div>
                    </div>
                    <div className="d-flex mb-3">
                      <div className="bg-light rounded-3 p-3 me-3" style={{ maxWidth: '70%' }}>
                        <p className="mb-0">I found a deluxe king room. Book it?</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-light">
                    <Form.Control type="text" placeholder="Type a message..." disabled />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-5 bg-light text-dark">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <Badge bg="primary" pill className="mb-3 px-3 py-2 fw-normal">
                CUSTOMER STORIES
              </Badge>
              <h2 className="fw-bold mb-3">What Our Customers Say</h2>
              <p className="text-muted">
                Don't just take our word for it. Here's what businesses using CallManager.ai have to say.
              </p>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <Carousel activeIndex={activeTestimonial} onSelect={setActiveTestimonial} indicators={false} interval={null}>
                {testimonials.map((testimonial, i) => (
                  <Carousel.Item key={i}>
                    <Card className="border-0 bg-white text-center p-5">
                      <Card.Body>
                        <div className="mb-4">
                          {renderStars(testimonial.rating)}
                        </div>
                        <blockquote className="blockquote mb-4">
                          <p className="lead fw-normal text-dark">"{testimonial.quote}"</p>
                        </blockquote>
                        <div>
                          <h5 className="mb-1 fw-bold text-dark">{testimonial.author}</h5>
                          <p className="text-muted mb-0">{testimonial.position}</p>
                        </div>
                      </Card.Body>
                    </Card>
                  </Carousel.Item>
                ))}
              </Carousel>
              <div className="text-center mt-4">
                {testimonials.map((_, i) => (
                  <Button
                    key={i}
                    variant={activeTestimonial === i ? "primary" : "outline-primary"}
                    className="rounded-circle mx-1"
                    style={{ width: '12px', height: '12px', padding: 0 }}
                    onClick={() => setActiveTestimonial(i)}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="py-5 bg-primary text-white">
        <Container>
          <Row>
            <Col md={3} className="text-center mb-4 mb-md-0">
              <h2 className="display-4 fw-bold mb-2">24/7</h2>
              <p className="mb-0">Availability</p>
            </Col>
            <Col md={3} className="text-center mb-4 mb-md-0">
              <h2 className="display-4 fw-bold mb-2">90%</h2>
              <p className="mb-0">Call Resolution</p>
            </Col>
            <Col md={3} className="text-center mb-4 mb-md-0">
              <h2 className="display-4 fw-bold mb-2">4.8/5</h2>
              <p className="mb-0">Satisfaction Score</p>
            </Col>
            <Col md={3} className="text-center">
              <h2 className="display-4 fw-bold mb-2">30s</h2>
              <p className="mb-0">Response Time</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <Badge bg="light" text="dark" pill className="mb-3 px-3 py-2 fw-normal">
                HAVE QUESTIONS?
              </Badge>
              <h2 className="fw-bold mb-3">Frequently Asked Questions</h2>
              <p className="text-muted">
                Common queries about CallManager.ai answered.
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg={8}>
              <Accordion defaultActiveKey="0" flush>
                {faqs.map((faq, i) => (
                  <Accordion.Item eventKey={i.toString()} key={i} className="mb-3 border-0 shadow-sm">
                    <Accordion.Header className="bg-white rounded-3">
                      <span className="fw-bold">{faq.question}</span>
                    </Accordion.Header>
                    <Accordion.Body className="bg-white rounded-bottom-3">
                      {faq.answer}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section id="demo" className="py-5 bg-white">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} className="bg-primary rounded-4 overflow-hidden shadow-lg">
              <Row className="align-items-center">
                <Col lg={6} className="p-5 text-white">
                  <h2 className="fw-bold mb-3">Ready to Revolutionize Service?</h2>
                  <p className="mb-4">
                    Book a demo to see how Kleo can transform your operations.
                  </p>
                  <ul className="list-unstyled mb-4">
                    <li className="mb-2 d-flex align-items-center">
                      <BsFillPatchCheckFill className="me-2" /> No commitment needed
                    </li>
                    <li className="mb-2 d-flex align-items-center">
                      <BsFillPatchCheckFill className="me-2" /> Tailored solutions
                    </li>
                    <li className="d-flex align-items-center">
                      <BsFillPatchCheckFill className="me-2" /> Live in weeks
                    </li>
                  </ul>
                </Col>
                <Col lg={6} className="bg-white p-5">
                  <h3 className="fw-bold mb-4">Book a Free Consultation</h3>
                  <Form onSubmit={handleSubmitDemo}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Your Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        aria-required="true"
                        placeholder="Enter your name"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        aria-required="true"
                        placeholder="Enter your email"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Company Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        aria-required="true"
                        placeholder="Enter your company"
                      />
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">Industry</Form.Label>
                      <Form.Select
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        required
                        aria-required="true"
                      >
                        <option value="">Select Industry</option>
                        <option>Hospitality</option>
                        <option>Real Estate</option>
                        <option>Healthcare</option>
                        <option>Retail</option>
                        <option>Other</option>
                      </Form.Select>
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100 py-3 fw-bold">
                      Book a Free Consultation
                    </Button>
                  </Form>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-5">
        <Container>
          <Row>
            <Col lg={4} className="mb-4 mb-lg-0">
              <div className="d-flex align-items-center mb-3">
                <FaRobot size={28} className="me-2 text-primary" aria-hidden="true" />
                <span className="fw-bold fs-4">CallManager.ai</span>
              </div>
              <p className="text-light mb-4">
                Ethical AI for efficient, human-centered service.
              </p>
            </Col>
            <Col md={4} lg={2} className="mb-4 mb-md-0">
              <h5 className="fw-bold mb-3">Product</h5>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Button variant="link" className="text-light text-decoration-none small p-0" onClick={() => scrollTo('features')}>
                    Features
                  </Button>
                </li>
                <li className="mb-2">
                  <Button variant="link" className="text-light text-decoration-none small p-0" onClick={() => scrollTo('industries')}>
                    Industries
                  </Button>
                </li>
                <li className="mb-2">
                  <Button variant="link" className="text-light text-decoration-none small p-0" onClick={() => scrollTo('demo')}>
                    Book a Free Consultation
                  </Button>
                </li>
              </ul>
            </Col>
            <Col md={4} lg={2} className="mb-4 mb-md-0">
              <h5 className="fw-bold mb-3">Resources</h5>
              <ul className="list-unstyled">
                <li className="mb-2"><Button variant="link" className="text-light text-decoration-none small p-0">Documentation</Button></li>
                <li className="mb-2"><Button variant="link" className="text-light text-decoration-none small p-0">Blog</Button></li>
                <li className="mb-2"><Button variant="link" className="text-light text-decoration-none small p-0">Support</Button></li>
              </ul>
            </Col>
            <Col md={4} lg={4}>
              <h5 className="fw-bold mb-3">Contact Us</h5>
              <ul className="list-unstyled text-light">
                <li className="mb-2 d-flex align-items-center">
                  <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                  123 AI Street, Tech City, TC 10101
                </li>
                <li className="mb-2 d-flex align-items-center">
                  <i className="fas fa-phone me-2 text-primary"></i>
                  +1 (555) 123-4567
                </li>
                <li className="d-flex align-items-center">
                  <i className="fas fa-envelope me-2 text-primary"></i>
                  info@callmanager.ai
                </li>
              </ul>
            </Col>
          </Row>
          <hr className="my-4 bg-light opacity-10" />
          <Row className="align-items-center">
            <Col md={6} className="mb-3 mb-md-0">
              <p className="mb-0 small text-light">
                &copy; {new Date().getFullYear()} CallManager.ai by Kleotech.ai. All rights reserved.
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <ul className="list-inline mb-0">
                <li className="list-inline-item">
                  <Button variant="link" className="text-light text-decoration-none small p-0">Privacy Policy</Button>
                </li>
                <li className="list-inline-item mx-2">·</li>
                <li className="list-inline-item">
                  <Button variant="link" className="text-light text-decoration-none small p-0">Terms of Service</Button>
                </li>
              </ul>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Demo Modal */}
      <Modal show={showDemoModal} onHide={() => setShowDemoModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Book a Free Consultation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col lg={6} className="mb-4 mb-lg-0">
              <h5 className="fw-bold mb-3">Experience Kleo Live</h5>
              <p className="text-muted mb-4">
                Our 30-minute demo showcases how CallManager.ai elevates your service.
              </p>
              <ul className="list-unstyled mb-4">
                <li className="mb-3 d-flex align-items-start">
                  <BsFillPatchCheckFill className="text-primary mt-1 me-2" />
                  <span>Explore industry-specific use cases</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <BsFillPatchCheckFill className="text-primary mt-1 me-2" />
                  <span>Talk to our experts about your needs</span>
                </li>
                <li className="d-flex align-items-start">
                  <BsFillPatchCheckFill className="text-primary mt-1 me-2" />
                  <span>Get all your questions answered</span>
                </li>
              </ul>
              <div className="bg-light p-3 rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <MdOutlineSupportAgent size={24} className="text-primary me-2" aria-hidden="true" />
                  <span className="fw-bold">Need help now?</span>
                </div>
                <p className="small text-muted mb-0">
                  Call +1 (555) 123-4567 or email support@callmanager.ai
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <CalendarComponent 
                onAppointmentScheduled={(event) => {
                  console.log('Appointment scheduled:', event);
                  setShowDemoModal(false);
                  alert('Appointment scheduled successfully! Check your email for confirmation.');
                }}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {/* Industry Demo Modal */}
      <Modal show={!!showIndustryModal} onHide={() => setShowIndustryModal(null)} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">{showIndustryModal?.title} Live Demo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Enter your phone number to try Kleo for {showIndustryModal?.title}.
          </p>
          <Form onSubmit={(e) => { e.preventDefault(); requestIndustryDemo(showIndustryModal); }}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Phone Number</Form.Label>
              <Form.Control
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+12345678901"
                required
                aria-required="true"
                aria-describedby="phoneHelp"
              />
              <Form.Text id="phoneHelp" muted>
                Use E.164 format (e.g., +12345678901).
              </Form.Text>
            </Form.Group>
            {callStatus === 'error' && (
              <Alert variant="danger" className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                Invalid phone number or request failed.
              </Alert>
            )}
            {callStatus === 'success' && (
              <Alert variant="success" className="d-flex align-items-center">
                <FaCheckCircle className="me-2" />
                Demo requested! Expect a call soon.
              </Alert>
            )}
            <Button
              variant="primary"
              type="submit"
              className="w-100 py-3 fw-bold"
              disabled={callStatus === 'loading' || !phoneInput}
            >
              {callStatus === 'loading' ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Requesting...
                </>
              ) : (
                'Request Live Demo'
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ChatBot */}
      <ChatBot isOpen={showChatBot} onToggle={() => setShowChatBot(!showChatBot)} />

      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 9999,
          padding: '10px',
          background: debugMode ? '#ff5722' : '#607d8b',
          color: 'white',
          borderRadius: '5px'
        }}>
          <button 
            onClick={() => setDebugMode(!debugMode)}
            style={{ background: 'none', border: 'none', color: 'white' }}
          >
            {debugMode ? '🔴 Debug ON' : '⚪ Debug OFF'}
          </button>
          {debugMode && (
            <div style={{ marginTop: '10px' }}>
              <div>Phone: {phoneInput}</div>
              <div>Industry: {JSON.stringify(showIndustryModal)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
